import React, { useState } from 'react';
import { Download, ScrollText } from 'lucide-react';
import { Badge, EmptyState, ErrorState, LoadingState } from '@/components/primitives';
import { Button } from '@/components/ui/button';
import { adminApi, downloadCsv, formatUtc, plural, useAdminQuery } from './adminApi';
import { AdminSection, Field, Pager, RefreshError, SearchField, Toolbar } from './AdminUi';

const PAGE = 50;

/**
 * The audit trail: who did what, when. Filters run on the server so
 * "all admin deletions last week" is one query, not a scroll.
 */
export default function AuditTab() {
  const [q, setQ] = useState('');
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [offset, setOffset] = useState(0);

  const params = {
    q: q.trim() || undefined,
    action: action.trim() || undefined,
    user_id: actor === 'admin' ? 'admin' : undefined,
    since: since || undefined,
    until: until || undefined,
    limit: PAGE,
    offset,
  };
  const { data, error, loading, reload } = useAdminQuery(
    () => adminApi.get('/admin/audit-logs', { params }).then((r) => r.data),
    [q, action, actor, since, until, offset]
  );

  const reset = (setter) => (e) => { setter(e.target.value); setOffset(0); };
  const logs = data?.logs || [];
  const filtered = Boolean(q || action || actor || since || until);

  const exportCsv = () => downloadCsv(
    `audit-log-${new Date().toISOString().slice(0, 10)}.csv`,
    ['Time (UTC)', 'Actor', 'Action', 'Summary', 'Targets', 'Details'],
    logs.map((l) => [l.timestamp, l.user_email, l.action, l.summary, (l.target_ids || []).join(' '), l.details]),
  );

  return (
    <AdminSection
      title="Audit log"
      count={data ? data.total : null}
      description="Sign-ins, moderation, deletions and other sensitive actions, newest first. Times are UTC."
      actions={<Button size="sm" variant="outline" onClick={exportCsv} disabled={!logs.length}><Download aria-hidden="true" /> Export this page (CSV)</Button>}
    >
      <Toolbar>
        <SearchField value={q} onChange={(v) => { setQ(v); setOffset(0); }} label="Search the audit log" placeholder="Search action, summary, target or user" />
      </Toolbar>
      <div className="adm-filters">
        <Field label="Action (exact)">{(id) => <input id={id} className="adm-input adm-mono" value={action} onChange={reset(setAction)} placeholder="e.g. company_delete" />}</Field>
        <Field label="Actor">
          {(id) => (
            <select id={id} className="adm-input adm-select" value={actor} onChange={reset(setActor)}>
              <option value="">Anyone</option>
              <option value="admin">Admin console</option>
            </select>
          )}
        </Field>
        <Field label="From">{(id) => <input id={id} type="date" className="adm-input" value={since} onChange={reset(setSince)} />}</Field>
        <Field label="Through">{(id) => <input id={id} type="date" className="adm-input" value={until} onChange={reset(setUntil)} />}</Field>
      </div>

      {loading ? (
        <LoadingState label="audit entries" rows={6} />
      ) : error && !data ? (
        <ErrorState title="Couldn't load the audit log" error={error} onRetry={reload} />
      ) : !logs.length ? (
        <EmptyState icon={ScrollText} title={filtered ? 'No entries match these filters' : 'Nothing has been logged yet'} description={filtered ? 'Widen the dates or clear a filter.' : 'Sensitive actions will appear here as they happen.'} />
      ) : (
        <>
          <RefreshError error={error} onRetry={reload} what="the audit log" />
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--stack" data-testid="audit-table">
              <thead>
                <tr><th scope="col">Time (UTC)</th><th scope="col">Actor</th><th scope="col">Action</th><th scope="col">What happened</th></tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td data-label="Time" className="adm-mono adm-nowrap adm-secondary">{formatUtc(l.timestamp, { seconds: true })}</td>
                    <td data-label="Actor" className="adm-break">
                      <span className="adm-mono">{l.user_email}</span>{' '}
                      {l.actor === 'admin' && <Badge size="sm" tone="warning" plain>Admin</Badge>}
                    </td>
                    <td data-label="Action" className="adm-mono adm-break">{l.action}</td>
                    <td data-label="Details" className="adm-details-cell adm-break">
                      {l.summary || <span className="adm-muted">No summary</span>}
                      {(l.target_ids?.length > 0 || (l.details && Object.keys(l.details).length > 0)) && (
                        <details>
                          <summary className="adm-link-btn">Details</summary>
                          {l.target_ids?.length > 0 && <div className="adm-meta">Targets: <span className="adm-mono">{l.target_ids.join(', ')}</span></div>}
                          {l.details && Object.keys(l.details).length > 0 && <pre className="adm-json">{JSON.stringify(l.details, null, 2)}</pre>}
                        </details>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} limit={PAGE} total={data.total} onChange={setOffset} label="Audit log pagination" />
          <span className="sr-only" aria-live="polite">{plural(data.total, 'entry', 'entries')}</span>
        </>
      )}
    </AdminSection>
  );
}
